import { useEffect, useState } from 'react'
import { useForm } from 'react-hook-form'
import { Link } from 'react-router'
import { toast } from 'sonner'

import { AuthFormShell } from '@/components/PageHeader'
import { AuthPasswordToggle } from '@/components/auth/AuthPasswordToggle'
import { AuthSubmitButton } from '@/components/auth/AuthSubmitButton'
import { LinkedInContinueButton } from '@/components/auth/LinkedInContinueButton'
import { GoogleContinueButton } from '@/components/auth/GoogleContinueButton'
import { AuthDivider } from '@/components/auth/AuthDivider'
import {
  authFieldClassName,
  authFooterTextClassName,
  authFormClassName,
  authFormNarrowClassName,
  authLabelClassName,
  authLinkClassName,
  authPasswordFieldClassName,
} from '@/components/auth/authStyles'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { useAuth } from '@/lib/auth'
import { cn } from '@/lib/utils'
import { ErrorCode } from '@paladin-monorepo/common'
import { useTranslation } from 'react-i18next'
import API from '@/lib/api'

interface LoginFormProps {
  email: string | null
  onSuccess?: () => void
}

interface LoginFormValues {
  email: string
  password: string
}

export function LoginForm({
  email,
  onSuccess,
  className,
  ...props
}: React.ComponentProps<'div'> & LoginFormProps) {
  const { t } = useTranslation()
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [showPassword, setShowPassword] = useState(false)
  const {
    handleSubmit,
    formState: { errors },
    getValues,
    register,
    setFocus,
  } = useForm<LoginFormValues>({
    defaultValues: {
      email: email || undefined,
    },
  })

  const { login } = useAuth({
    middleware: 'guest',
    redirectTo: '/monomo-ai',
    tmp: 'LoginForm',
  })

  const handleResendVerification = () => {
    API.post('/auth/resend-verification', { email: getValues('email') })
      .then(() => {
        toast.success(t('login.resendVerification.success'), {
          duration: 5000,
          closeButton: true,
        })
      })
      .catch((error) => {
        console.error(error)

        const { code } = error.response.data;

        if (code === 42900) {
          toast.error(t('login.resendVerification.error.rateLimited'), {
            duration: 5000,
            closeButton: true,
          })
        } else {
          toast.error(t('login.resendVerification.error.generic'), {
            duration: 5000,
            closeButton: true,
          })
        }
      })
  }

  const onSubmit = async (values: LoginFormValues) => {
    if (isSubmitting) return
    if (Object.keys(errors).length > 0) return
    if (!values.email || !values.password) return

    setIsSubmitting(true)

    return login({
      values,
      setError: () => void 0,
    })
      .then(() => {
        onSuccess?.()
      })
      .catch((error) => {
        console.error(error)

        const { code } = error.response.data;

        if (code === ErrorCode.EmailNotVerified) {
          toast.error(t('login.errors.emailNotVerified.title'), {
            description: t('login.errors.emailNotVerified.description'),
            action: {
              label: t('login.resendVerification.button'),
              onClick: handleResendVerification,
            },
            duration: 5000,
            closeButton: true,
          })
        } else if (code === ErrorCode.LinkedinOnlySignInMethod) {
          toast.error(t('auth.linkedin.errors.linkedinOnlySignIn'), {
            duration: 5000,
            closeButton: true,
          })
        } else if (code === ErrorCode.GoogleOnlySignInMethod) {
          toast.error(t('auth.google.errors.googleOnlySignIn'), {
            duration: 5000,
            closeButton: true,
          })
        } else if (code === ErrorCode.SsoOnlySignInMethod) {
          toast.error(t('auth.google.errors.ssoOnlySignIn'), {
            duration: 5000,
            closeButton: true,
          })
        } else {
          toast.error(t('login.errors.invalidCredentials'), {
            duration: 5000,
            closeButton: true,
          })
        }
      })
      .finally(() => {
        setIsSubmitting(false)
      })
  }

  useEffect(() => {
    if (email && setFocus) {
      setFocus('password')
    }
  }, [email, setFocus])

  return (
    <AuthFormShell
      className={cn(authFormNarrowClassName, className)}
      titleDisplay={t('login.titleDisplay')}
      titleSans={t('login.titleSans')}
      description={t('login.description')}
      {...props}
    >
      <form onSubmit={handleSubmit(onSubmit)} className={authFormClassName}>
        <div className="space-y-[7px]">
          <Label htmlFor="email" className={authLabelClassName}>
            {t('login.fields.email.label')}
          </Label>
          <Input
            className={cn(authFieldClassName, errors.email && 'border-destructive')}
            id="email"
            type="email"
            placeholder={t('login.fields.email.placeholder')}
            disabled={isSubmitting}
            autoComplete="email"
            autoFocus
            tabIndex={1}
            {...register('email', {
              required: 'login.fields.email.errors.required',
            })}
          />
          {errors.email && (
            <p className="text-sm text-destructive">{t(errors.email.message as string)}</p>
          )}
        </div>

        <div className="space-y-4">
          <div className="flex items-center justify-between gap-2">
            <Label htmlFor="password" className={authLabelClassName}>
              {t('login.fields.password.label')}
            </Label>
            <Link to="/auth/forgot-password" className={authLinkClassName}>
              {t('login.forgotPasswordLink')}
            </Link>
          </div>
          <div className="relative">
            <Input
              className={cn(
                authFieldClassName,
                authPasswordFieldClassName,
                'pr-12',
                errors.password && 'border-destructive',
              )}
              id="password"
              type={showPassword ? 'text' : 'password'}
              placeholder="••••••••••"
              disabled={isSubmitting}
              autoComplete="current-password"
              tabIndex={1}
              {...register('password', {
                required: 'login.fields.password.errors.required',
              })}
            />
            <AuthPasswordToggle
              showPassword={showPassword}
              onToggle={() => setShowPassword((value) => !value)}
            />
          </div>
          {errors.password && (
            <p className="text-sm text-destructive">{t(errors.password.message as string)}</p>
          )}
        </div>

        <AuthSubmitButton disabled={isSubmitting} type="submit" tabIndex={2}>
          {isSubmitting ? t('login.submitting') : t('login.submit')}
        </AuthSubmitButton>

        <AuthDivider />
        <LinkedInContinueButton disabled={isSubmitting} tabIndex={3} />
        <GoogleContinueButton disabled={isSubmitting} tabIndex={4} />

        <p className={cn(authFooterTextClassName, 'text-center')}>
          {t('login.noAccountQuestion')}{' '}
          <Link to="/auth/signup" className={authLinkClassName}>
            {t('login.signupLink')}
          </Link>
        </p>
      </form>
    </AuthFormShell>
  )
}
